import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { Eye, Package, ScanLine } from 'lucide-react';
import { Button } from '@/shared/ui/buttons/Button';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { DataTable } from '@/shared/ui/data-table/DataTable';
import { Pagination } from '@/shared/ui/data-table/Pagination';
import { FilterBar } from '@/shared/ui/data-table/FilterBar';
import { extractErrorMessage } from '@/shared/api/errors';
import { useArrivedPackagesList } from '@/entities/warehouse-arrivals';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { usePerm } from '@/shared/hooks/usePerm';
import { DeliveredToggle, WarehouseArrivalsScanDialog } from '@/features/warehouse-arrivals';
import type { ApiArrivedPackageItem } from '@/shared/types/api/warehouse-arrivals';

const PAGE_SIZE = 20;

export const WarehouseArrivedPackagesTab = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const canMarkDelivered = usePerm('warehouse.mark_delivered');
  const canScan = usePerm('warehouse.scan');

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [scanOpen, setScanOpen] = useState(false);

  const params = useMemo(
    () => ({
      page,
      page_size: PAGE_SIZE,
      search: debouncedSearch.trim() || undefined,
    }),
    [page, debouncedSearch],
  );

  const query = useArrivedPackagesList(params);

  const columns = useMemo<ColumnDef<ApiArrivedPackageItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('warehouseArrivals.packages.columns.name'),
        cell: ({ row }) => (
          <span className="block truncate font-medium text-ink">{row.original.name || '—'}</span>
        ),
      },
      {
        id: 'party',
        enableSorting: false,
        header: t('warehouseArrivals.packages.columns.party'),
        cell: ({ row }) => row.original.party?.name || '—',
      },
      {
        id: 'parcels',
        enableSorting: false,
        header: t('warehouseArrivals.packages.columns.parcels'),
        cell: ({ row }) => {
          const ps = row.original.parcels;
          if (!ps.length) return '—';
          return (
            <span className="block truncate">
              {ps.map((p) => p.trek_id || p.name || '—').join(', ')}
            </span>
          );
        },
      },
      {
        id: 'delivered',
        enableSorting: false,
        header: t('warehouseArrivals.packages.columns.delivered'),
        cell: ({ row }) => (
          <DeliveredToggle
            packageId={row.original.id}
            value={row.original.delivered_to_customer}
            canEdit={canMarkDelivered}
          />
        ),
      },
      {
        id: 'actions',
        enableSorting: false,
        header: t('warehouseArrivals.packages.columns.actions'),
        cell: ({ row }) => (
          <IconButton
            label={t('warehouseArrivals.packages.view')}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/warehouse/arrived-packages/${row.original.id}`);
            }}
          >
            <Eye className="h-4 w-4" />
          </IconButton>
        ),
      },
    ],
    [navigate, t, canMarkDelivered],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <FilterBar
          searchValue={search}
          onSearchChange={(v) => {
            setPage(1);
            setSearch(v);
          }}
          searchPlaceholder={t('warehouseArrivals.packages.searchPlaceholder')}
        />
        {canScan && (
          <Button
            variant="outline"
            onClick={() => setScanOpen(true)}
            leftIcon={<ScanLine className="h-4 w-4" />}
            title={t('warehouseArrivals.packages.scan')}
            aria-label={t('warehouseArrivals.packages.scan')}
          >
            <span className="hidden sm:inline">{t('warehouseArrivals.packages.scan')}</span>
          </Button>
        )}
      </div>

      {query.error ? (
        <ErrorState
          message={extractErrorMessage(query.error, t('warehouseArrivals.packages.loadError'))}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <>
          <DataTable
            data={query.data?.results ?? []}
            columns={columns}
            isLoading={query.isLoading}
            emptyTitle={t('warehouseArrivals.packages.emptyTitle')}
            emptyDescription={t('warehouseArrivals.packages.emptyDescription')}
            emptyIcon={<Package className="h-8 w-8" aria-hidden />}
            onRowClick={(row) => navigate(`/warehouse/arrived-packages/${row.id}`)}
            getRowId={(row) => row.id}
            numbered
            pageIndex={page - 1}
            pageSize={PAGE_SIZE}
          />
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={query.data?.count ?? 0}
            onPageChange={setPage}
          />
        </>
      )}

      <WarehouseArrivalsScanDialog open={scanOpen} onClose={() => setScanOpen(false)} />
    </div>
  );
};
